import React, { useCallback, useRef, useEffect, useState } from 'react'
import { DatePicker, Button, ConfigProvider } from 'antd'
import dayjs from 'dayjs'
import locale from 'antd/es/locale/ru_RU'
import { IDate } from './interface'

export function InputDate({ value, onChange, UISchema }: IDate) {
  const ref = useRef<any>(null)
  const [localValue, setLocalValue] = useState<string | null>(null)
  const disabled = Boolean(UISchema?.options?.disabled)
  const style = UISchema?.style
  const picker = UISchema?.options?.picker ?? ''
  const format = UISchema?.options?.format ?? 'DD.MM.YYYY'
  const isDisabledDatePrev = UISchema?.options?.isDisabledDatePrev ?? false
  const isDisabledDateNext = UISchema?.options?.isDisabledDateNext ?? false
  const disabledDateInc = UISchema?.options?.disabledDate || []
  const placeholder = UISchema?.options?.placeholder

  useEffect(() => {
    setLocalValue(value || null)
  }, [value])

  // Синхронизируем localValue с внешним value
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value || null)
    }
  }, [value, localValue])

  const disabledDate = useCallback(
    (date: dayjs.Dayjs) => {
      if (!date) return false
      if (disabledDateInc.includes(date.format('YYYY-MM-DD').toString())) return true

      const getDateValue = (input: boolean | string, defaultDate: dayjs.Dayjs) =>
        typeof input === 'string' ? dayjs(input) : input ? defaultDate : null

      const prevDate = getDateValue(isDisabledDatePrev, dayjs().startOf('day'))
      const nextDate = getDateValue(isDisabledDateNext, dayjs().endOf('day'))

      if (prevDate && date.valueOf() < prevDate.valueOf()) return true
      if (nextDate && date.valueOf() > nextDate.valueOf()) return true

      return false
    },
    [isDisabledDatePrev, isDisabledDateNext, disabledDateInc],
  )

  const setYesterday = useCallback(() => {
    const yesterday = dayjs().subtract(1, 'days').startOf('day').format('YYYY-MM-DD')
    onChange?.(yesterday)
    if (ref.current && 'blur' in ref.current && typeof ref.current.blur === 'function') {
      ref.current.blur()
    }
  }, [onChange])

  const setTomorrow = useCallback(() => {
    const tomorrow = dayjs().add(1, 'day').startOf('day').format('YYYY-MM-DD')
    onChange?.(tomorrow)
    if (ref.current && 'blur' in ref.current && typeof ref.current.blur === 'function') {
      ref.current.blur()
    }
  }, [onChange])

  const setWeekLater = useCallback(() => {
    const weekLater = dayjs().add(7, 'days').startOf('day').format('YYYY-MM-DD')
    onChange?.(weekLater)
    if (ref.current && 'blur' in ref.current && typeof ref.current.blur === 'function') {
      ref.current.blur()
    }
  }, [onChange])

  const buttonStyle = { border: 'unset', color: '#40a9ff', padding: 0 }

  const Footer = useCallback(() => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        {isDisabledDatePrev && !isDisabledDateNext ? (
          <Button style={buttonStyle} onClick={setTomorrow}>
            Завтра
          </Button>
        ) : (
          <Button style={buttonStyle} onClick={setYesterday}>
            Вчера
          </Button>
        )}
        {!isDisabledDateNext && (
          <Button style={buttonStyle} onClick={setWeekLater}>
            Через неделю
          </Button>
        )}
      </div>
    )
  }, [setWeekLater, setYesterday, setTomorrow, isDisabledDatePrev, isDisabledDateNext])

  const handleChange = useCallback(
    (nextValue: dayjs.Dayjs | null) => {
      if (nextValue) {
        const localDate = nextValue.startOf('day').format('YYYY-MM-DD')
        setLocalValue(localDate)
        onChange?.(localDate)
      } else {
        setLocalValue(null)
        onChange?.(null)
      }
    },
    [onChange],
  )

  const datePickerProps = {
    ref,
    renderExtraFooter: Footer,
    onChange: handleChange,
    format,
    value: localValue ? dayjs(localValue) : null,
    disabledDate,
    disabled,
    style,
    placeholder,
    ...(picker && { picker }),
  }

  return (
    <ConfigProvider locale={locale}>
      <DatePicker {...datePickerProps} />
    </ConfigProvider>
  )
}

export default InputDate
